import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import { Button } from '@/components/ui/button';
import { Check, Shield, FileCheck, Search, TrendingUp, Award, Clock, Users } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkRegistrationPage() {
  const [contactFormOpen, setContactFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation onOpenContactForm={() => setContactFormOpen(true)} />
      <ContactFormDialog open={contactFormOpen} onOpenChange={setContactFormOpen} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/345bb8f2-83fc-43aa-bc1a-7e6d1f8dce2e/images/afc1a0b16e58c295aa141cac150995d3ee9ae43bc6384dcc4053a825cbdbecb1.png)' }} data-testid="service-hero">
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Professional Trademark Registration Services
            </h1>
            <p className="text-lg lg:text-xl text-slate-200 leading-relaxed mb-8">
              Secure your brand with expert federal trademark registration. Our USPTO-registered attorneys provide comprehensive trademark services from search to registration, ensuring your intellectual property receives maximum protection.
            </p>
            <Button onClick={() => setContactFormOpen(true)} className="bg-[#B4905A] hover:bg-[#927448] text-white px-10 py-6 text-lg font-semibold" data-testid="service-hero-cta">
              Start My Registration
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Complete Trademark Registration Services in the USA
              </h2>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                Trademark registration is essential for protecting your brand name, logo, slogan, or any distinctive mark that identifies your business. Our comprehensive trademark registration services guide you through every step of the federal trademark filing process with the United States Patent and Trademark Office (USPTO).
              </p>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                With over 15 years of experience in trademark law and intellectual property protection, our team of USPTO-registered trademark attorneys has successfully filed over 50,000 trademark applications. We understand the complexities of trademark registration and work diligently to ensure your application is prepared correctly, filed promptly, and approved efficiently.
              </p>
              <p className="text-base leading-relaxed text-slate-600">
                Whether you're registering your first trademark or expanding your intellectual property portfolio, our trademark services provide the expertise, guidance, and support you need to protect your brand nationwide.
              </p>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsYXclMjBvZmZpY2UlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzc1MjM5MDI1fDA&ixlib=rb-4.1.0&q=85" alt="Trademark Registration" className="w-full h-[500px] object-cover border border-slate-200" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Benefits of Federal Trademark Registration
            </h2>
            <p className="text-base leading-relaxed text-slate-600">
              Registering your trademark with the USPTO provides powerful legal protections and business advantages that help secure your brand's future.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: Shield, title: 'Nationwide Protection', desc: 'Federal registration provides exclusive rights to use your trademark across all 50 states, preventing competitors from using confusingly similar marks.' },
              { icon: Award, title: 'Legal Presumption of Ownership', desc: 'Registration creates a legal presumption that you own the mark and have the exclusive right to use it for your goods or services.' },
              { icon: TrendingUp, title: 'Increased Brand Value', desc: 'A registered trademark is a valuable business asset that increases your company\'s valuation and can be licensed or sold.' },
              { icon: FileCheck, title: 'Right to Use ® Symbol', desc: 'Only federally registered trademarks can use the ® symbol, signaling official registration and deterring infringement.' },
              { icon: Users, title: 'Federal Court Access', desc: 'Registration allows you to file trademark infringement lawsuits in federal court and potentially recover attorney fees and damages.' },
              { icon: Clock, title: 'Long-Term Protection', desc: 'Trademark rights last indefinitely as long as you continue using the mark and file required maintenance documents.' },
            ].map((benefit, index) => (
              <div key={index} className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <benefit.icon className="h-10 w-10 text-[#1E3A8A] mb-4" />
                <h3 className="text-xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{benefit.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Trademark Registration Process
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { num: '01', title: 'Comprehensive Trademark Search', desc: 'We conduct thorough searches of federal, state, and common law databases to identify potential conflicts and assess registration viability.' },
              { num: '02', title: 'Expert Application Preparation', desc: 'Our trademark attorneys prepare your application with precise legal descriptions, proper classifications, and all required documentation.' },
              { num: '03', title: 'Filing & Registration Support', desc: 'We file your application with the USPTO, monitor progress, handle office actions, and ensure successful registration.' },
            ].map((step, index) => (
              <div key={index} className="process-step bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="text-5xl font-bold text-[#B4905A] mb-4 opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</div>
                <h3 className="text-xl lg:text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trademark Registration Pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Essentials', price: '$45', features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured'] },
              { name: 'Enhanced', price: '$149', features: ['Case Review', 'Case Filing', 'Direct Hit Search', 'Trademark Alert', 'Trademark Secured', 'High Satisfaction Rate', 'Refusal Risk Meter'] },
              { name: 'Deluxe', price: '$299', popular: true, features: ['Case Review', 'Case Filing', 'Direct Hit Search', 'Trademark Alert', 'Trademark Secured', 'High Satisfaction Rate', 'Refusal Risk Meter', 'Trademark Monitoring', 'Complete Documentation', 'Digital File', 'Dedicated Case Manager'] },
              { name: 'Platinum', price: '$349', features: ['All Deluxe Features', 'High Approval Rate', 'Comprehensive Search Report', 'High Priority Service', 'Cease & Desist Letter'] },
            ].map((plan, index) => (
              <div key={index} className={`bg-white border-2 p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] flex flex-col ${plan.popular ? 'border-[#B4905A]' : 'border-slate-200'}`}>
                {plan.popular && <div className="bg-[#B4905A] text-white text-xs font-bold py-1 px-3 -mt-6 -mx-6 mb-4 text-center">RECOMMENDED</div>}
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.price}</span>
                  <span className="text-sm text-slate-600 block">+ Federal Filing Fee</span>
                </div>
                <div className="flex-1 mb-6 max-h-[280px] overflow-y-auto pr-2">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="h-4 w-4 text-[#1E3A8A] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button onClick={() => setContactFormOpen(true)} className={`w-full py-6 font-semibold ${plan.popular ? 'bg-[#B4905A] hover:bg-[#927448]' : 'bg-[#1E3A8A] hover:bg-[#1E40AF]'} text-white`}>
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trademark Registration FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'How long does trademark registration take?', a: 'The federal trademark registration process typically takes 8-12 months from filing to final registration. This includes USPTO examination, publication for opposition, and final approval. We provide updates throughout the process.' },
              { q: 'What can I trademark?', a: 'You can trademark brand names, logos, slogans, product names, service marks, and even sounds or colors that distinguish your business. Our attorneys help determine what aspects of your brand are eligible for trademark protection.' },
              { q: 'Do I need a trademark lawyer?', a: 'While not legally required, working with experienced trademark attorneys significantly increases your chances of approval. Our team ensures proper classification, comprehensive searches, and expert handling of any office actions or objections.' },
              { q: 'What is the cost of trademark registration?', a: 'Our trademark services range from $45 to $349 plus USPTO filing fees ($250-$350 per class). The total cost depends on the package you choose and the number of trademark classes for your goods or services.' },
              { q: 'Can I register a trademark for my business name?', a: 'Yes, you can register your business name as a trademark if you use it to identify your goods or services. Federal registration provides nationwide protection for your business name in connection with your specific offerings.' },
            ].map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-slate-200 px-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <AccordionTrigger className="text-left font-semibold text-slate-900">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600 pt-2">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Choose Us for Trademark Registration
          </h2>
          <p className="text-base leading-relaxed text-slate-600 max-w-3xl mx-auto mb-12">
            With 50,000+ successful trademark registrations, 98% approval rate, and 15+ years of experience, we are the trusted choice for businesses seeking professional trademark services. Our dedicated team provides personalized support from search to registration.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: FileCheck, value: '50,000+', label: 'Trademarks Filed' },
              { icon: Award, value: '98%', label: 'Success Rate' },
              { icon: Clock, value: '15+', label: 'Years Experience' },
              { icon: Users, value: '24/7', label: 'Support' },
            ].map((stat, index) => (
              <div key={index}>
                <stat.icon className="h-10 w-10 mx-auto mb-3 text-[#B4905A]" />
                <div className="text-3xl lg:text-4xl font-bold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Related Intellectual Property Services
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Trademark Search', desc: 'Comprehensive trademark search services to identify conflicts before filing your application.', link: '/trademark-search' },
              { title: 'Copyright Registration', desc: 'Protect your creative works with federal copyright registration services.', link: '/copyright-registration' },
              { title: 'Amazon Brand Registry', desc: 'Enroll in Amazon Brand Registry to protect your products from counterfeiters.', link: '/amazon-brand-registry' },
            ].map((service, index) => (
              <div key={index} className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <h3 className="text-xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600 mb-4">{service.desc}</p>
                <a href={service.link} className="text-[#1E3A8A] font-semibold text-sm">Learn More →</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trademark Registration Success Stories
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Tech Startup Successfully Registers Brand</h3>
              <p className="text-sm leading-relaxed text-slate-600 mb-4">A Silicon Valley startup needed urgent trademark protection before product launch. We conducted a comprehensive search, filed their application, and secured registration in 4 months.</p>
              <div className="text-sm font-semibold text-[#1E3A8A]">Industry: Technology | Timeline: 4 Months</div>
            </div>
            <div className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Restaurant Chain Protects Brand Nationwide</h3>
              <p className="text-sm leading-relaxed text-slate-600 mb-4">A growing restaurant chain needed federal trademark protection across multiple classes. We registered their name, logo, and signature dish names, providing comprehensive brand protection.</p>
              <div className="text-sm font-semibold text-[#1E3A8A]">Industry: Food & Beverage | Timeline: 5 Months</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Client Testimonials
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah Johnson', company: 'Tech Founder', text: 'Professional service from start to finish. Our trademark was approved without any issues!' },
              { name: 'Michael Chen', company: 'E-Commerce Owner', text: 'Best trademark service I\'ve used. Fast, affordable, and expert guidance throughout.' },
              { name: 'Emily Rodriguez', company: 'Restaurant Owner', text: 'Highly recommend for trademark registration. They made the process simple and stress-free.' },
            ].map((testimonial, index) => (
              <div key={index} className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <p className="text-sm leading-relaxed text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-sm text-slate-600">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section 
        className="relative py-20 lg:py-32 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzc1MjQ2NTUzfDA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-[#1E3A8A]/90"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Register Your Trademark?
          </h2>
          <p className="text-lg lg:text-xl text-slate-100 leading-relaxed mb-8">
            Protect your brand with professional trademark registration services. Get started today with our expert team and secure your intellectual property rights.
          </p>
          <Button onClick={() => setContactFormOpen(true)} className="bg-[#B4905A] hover:bg-[#927448] text-white px-10 py-6 text-lg font-semibold hover:-translate-y-1 transition-all" data-testid="final-cta">
            Start Registration Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
